import type { FC, ReactNode } from 'react'
import type { CSSVariables } from '@/types'

import { cn } from '@/lib/utils'

interface LogoMarkProps {
    className?: string
    animated?: boolean
    title?: string
}

// "AN" with a code tray on the first row, "SAM" on the second, drawn as rounded strokes.
const LETTER_PATHS = [
    'M10 100 L46 20 Q54 4 62 20 L98 100',
    'M24 70 H84',
    'M116 100 V28 Q116 12 132 12 H164 Q180 12 180 28 V100 H306 V54',
    'M92 135 C86 125 72 120 53 120 C30 120 14 130 14 146 C14 162 30 166 53 168 C78 170 94 176 94 190 C94 203 78 208 53 208 C33 208 17 202 9 190',
    'M112 208 L148 128 Q156 112 164 128 L200 208',
    'M126 178 H186',
    'M218 208 V136 Q218 120 234 120 H290 Q306 120 306 136 V208',
    'M262 120 V188'
]

const CODE_PATHS = [
    'M214 28 L199 46 L214 64',
    'M250 22 L234 70',
    'M270 28 L285 46 L270 64'
]

// The red "</>" from the logo on its own, used as a small brand glyph.
export const CodeGlyph: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='190 13 104 66'
            fill='none'
            stroke='currentColor'
            strokeWidth={13}
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            className={cn('block h-auto', className)}
        >
            {CODE_PATHS.map(d => (
                <path key={d} d={d} />
            ))}
        </svg>
    )
}

export const LogoMark: FC<LogoMarkProps> = ({ className, animated = false, title }): ReactNode => {
    return (
        <svg
            viewBox='0 0 316 220'
            fill='none'
            strokeWidth={13}
            strokeLinecap='round'
            strokeLinejoin='round'
            role={title ? 'img' : undefined}
            aria-hidden={title ? undefined : true}
            className={cn('block h-auto', className)}
        >
            {title && <title>{title}</title>}

            <g stroke='currentColor'>
                {LETTER_PATHS.map((d, index) => (
                    <path
                        key={d}
                        d={d}
                        pathLength={animated ? 1 : undefined}
                        className={animated ? 'intro-stroke' : undefined}
                        style={animated ? { '--i': index } as CSSVariables : undefined}
                    />
                ))}
            </g>

            <g stroke='var(--primary)' className={animated ? 'intro-code' : undefined}>
                {CODE_PATHS.map(d => (
                    <path key={d} d={d} />
                ))}
            </g>
        </svg>
    )
}
